import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RichTextEditor } from '@/components/RichTextEditor';
import { ImageUpload } from '@/components/ImageUpload';
import { TagSelector } from '@/components/TagSelector';
import { EventSelector } from '@/components/EventSelector';
import { EventDisplay } from '@/components/EventDisplay';
import { VotifierManager } from '@/components/VotifierManager';
import { WebhookManager } from '@/components/WebhookManager';
import { useEditServer } from '@/hooks/useEditServer';
import { useGames } from '@/hooks/useGames';
import { Trash2, Gamepad2, Crown, Lock, Sparkles } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogPortal
} from '@/components/ui/alert-dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { normalizeWebUrl } from '@/utils/normalizeUrl';

interface Server {
  id: string;
  name: string;
  description: string;
  ip_address: string;
  version?: string;
  website_url?: string;
  discord_url?: string;
  tags: string[];
  logo_image_url?: string;
  banner_image_url?: string;
  description_image_url?: string;
  custom_color?: string;
  game_id?: string;
  is_sponsored?: boolean;
  prestige_expires_at?: string;
  video_url?: string;
}

interface EditServerDialogProps {
  server: Server | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditServerDialog: React.FC<EditServerDialogProps> = ({ server, isOpen, onClose }) => {
  const { t } = useLanguage();
  const { editServer, deleteServer, isEditingServer, isDeletingServer } = useEditServer();
  const { data: games } = useGames();

  // État interne pour contrôler la dialog et empêcher sa fermeture pendant les opérations d'événements
  const [internalIsOpen, setInternalIsOpen] = useState(isOpen);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ip_address: '',
    version: '',
    website_url: '',
    discord_url: '',
    tags: [] as string[],
    logo_image_url: '',
    banner_image_url: '',
    description_image_url: '',
    custom_color: '',
    game_id: ''
  });

  useEffect(() => {
    if (server) {
      setFormData({
        name: server.name || '',
        description: server.description || '',
        ip_address: server.ip_address || '',
        version: server.version || '',
        website_url: server.website_url || '',
        discord_url: server.discord_url || '',
        tags: server.tags || [],
        logo_image_url: server.logo_image_url || '',
        banner_image_url: server.banner_image_url || '',
        description_image_url: server.description_image_url || '',
        custom_color: server.custom_color || '',
        game_id: server.game_id || ''
      });
    }
  }, [server]);

  // Synchroniser l'état interne avec la prop isOpen
  useEffect(() => {
    setInternalIsOpen(isOpen);
  }, [isOpen]);

  // Gestionnaire personnalisé pour fermer la dialog
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setInternalIsOpen(false);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!server) return;

    await editServer({
      serverId: server.id,
      data: {
        ...formData,
        website_url: normalizeWebUrl(formData.website_url),
        discord_url: normalizeWebUrl(formData.discord_url),
      }
    });

    onClose();
  };

  const handleDelete = async () => {
    if (!server) return;
    await deleteServer(server.id);
    onClose();
  };

  const handleLogoUpload = (url: string) => {
    setFormData((prev) => ({ ...prev, logo_image_url: url }));
  };

  const handleBannerUpload = (url: string) => {
    setFormData((prev) => ({ ...prev, banner_image_url: url }));
  };

  const handleDescriptionImageUpload = (url: string) => {
    setFormData((prev) => ({ ...prev, description_image_url: url }));
  };

  if (!server) return null;

  // Vérifier si le serveur est Prestige
  const isServerPrestige =
    server.is_sponsored && server.prestige_expires_at && new Date(server.prestige_expires_at) > new Date();

  return (
    <Dialog open={internalIsOpen} onOpenChange={handleDialogClose}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[96vw] max-w-6xl h-auto max-h-[95vh] overflow-y-auto overflow-x-hidden p-3 sm:p-4 md:p-6">
        <DialogHeader className="space-y-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pr-2 sm:pr-4">
            <DialogTitle className="text-white font-mono text-base sm:text-lg md:text-xl flex items-center gap-1.5 sm:gap-2 flex-wrap">
              {isServerPrestige && <Crown className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-yellow-400 flex-shrink-0" />}
              <span className="truncate max-w-[180px] sm:max-w-none">{t('editServer.editServer')} - {server?.name}</span>
              {isServerPrestige && (
                <div className="flex items-center gap-1 sm:gap-2">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 flex-shrink-0" />
                  <span className="text-xs sm:text-sm bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent font-bold whitespace-nowrap">
                    PRESTIGE
                  </span>
                </div>
              )}
            </DialogTitle>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isDeletingServer} className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3 flex-shrink-0 w-full sm:w-auto">
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  {t('editServer.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogPortal>
                <AlertDialogContent className="bg-slate-800 border-slate-700 z-[100] max-w-[90vw] md:max-w-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-white">{t('editServer.deleteConfirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-400">
                      {t('editServer.deleteConfirmText').replace('{name}', server?.name || '')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-slate-700 hover:bg-slate-600 text-white border-slate-600">
                      {t('button.cancel')}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      disabled={isDeletingServer}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {isDeletingServer ? t('editServer.deleting') : t('editServer.delete')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialogPortal>
            </AlertDialog>
          </div>
        </DialogHeader>

        {/* -------- FORM GLOBAL qui englobe TOUS les onglets -------- */}
        <form id="edit-server-form" onSubmit={handleSubmit}>
          <Tabs defaultValue="basic" className="w-full mt-2">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 bg-slate-800 border-slate-700 text-[10px] sm:text-xs md:text-sm h-auto py-1">
              <TabsTrigger value="basic" className="data-[state=active]:bg-slate-700 px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs md:text-sm">
                <span className="hidden sm:inline">{t('editServer.basicInfo')}</span>
                <span className="inline sm:hidden">Base</span>
              </TabsTrigger>
              <TabsTrigger value="media" className="data-[state=active]:bg-slate-700 px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs md:text-sm">
                <span className="hidden sm:inline">{t('editServer.mediaTags')}</span>
                <span className="inline sm:hidden">Media</span>
              </TabsTrigger>
              <TabsTrigger value="integrations" className="data-[state=active]:bg-slate-700 px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs md:text-sm">
                <span className="hidden sm:inline">{t('editServer.integrations')}</span>
                <span className="inline sm:hidden">API</span>
              </TabsTrigger>
              <TabsTrigger value="prestige" className="data-[state=active]:bg-slate-700 px-1 sm:px-2 py-1.5 sm:py-2 text-[10px] sm:text-xs md:text-sm">
                Prestige
              </TabsTrigger>
            </TabsList>

            {/* Onglet : Informations de base (SANS <form> local) */}
            <TabsContent value="basic" className="space-y-3 sm:space-y-4 md:space-y-6 mt-3 sm:mt-4 md:mt-6">
              {/* Informations de base */}
              <div className="bg-slate-800/50 p-3 sm:p-4 md:p-6 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-gaming-primary" />
                  <h3 className="font-bold text-gaming-primary font-mono text-sm sm:text-base">{t('editServer.basicInfo')}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-300 font-mono text-xs sm:text-sm">
                      {t('editServer.serverName')}
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="retro-input bg-slate-900/50 border-slate-600 focus:border-gaming-primary h-9 sm:h-10 text-sm"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="game" className="text-slate-300 font-mono text-xs sm:text-sm">
                      {t('editServer.game')}
                    </Label>
                    <Select value={formData.game_id} onValueChange={(value) => setFormData({ ...formData, game_id: value })}>
                      <SelectTrigger className="retro-input bg-slate-900/50 border-slate-600 focus:border-gaming-primary h-9 sm:h-10 text-sm">
                        <SelectValue placeholder={t('editServer.selectGame')} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        {games?.map((game) => (
                          <SelectItem key={game.id} value={game.id} className="text-white hover:bg-slate-700 text-sm">
                            {game.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                  <div>
                    <Label htmlFor="ip_address" className="text-slate-300 font-mono text-xs sm:text-sm">
                      {t('editServer.ipAddress')}
                    </Label>
                    <Input
                      id="ip_address"
                      value={formData.ip_address}
                      onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                      className="retro-input bg-slate-900/50 border-slate-600 focus:border-gaming-primary h-9 sm:h-10 text-sm"
                      placeholder="127.0.0.1:25565"
                    />
                  </div>

                  <div>
                    <Label htmlFor="version" className="text-slate-300 font-mono text-xs sm:text-sm">
                      {t('editServer.version')}
                    </Label>
                    <Input
                      id="version"
                      value={formData.version}
                      onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                      className="retro-input bg-slate-900/50 border-slate-600 focus:border-gaming-primary h-9 sm:h-10 text-sm"
                      placeholder="1.20.1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-3 sm:mt-4">
                  <div>
                    <Label htmlFor="website_url" className="text-slate-300 font-mono text-xs sm:text-sm">
                      {t('editServer.website')}
                    </Label>
                    <Input
                      id="website_url"
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      className="retro-input bg-slate-900/50 border-slate-600 focus:border-gaming-primary h-9 sm:h-10 text-sm"
                      placeholder="https://monserveur.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="discord_url" className="text-slate-300 font-mono text-xs sm:text-sm">
                      {t('editServer.discord')}
                    </Label>
                    <Input
                      id="discord_url"
                      type="url"
                      value={formData.discord_url}
                      onChange={(e) => setFormData({ ...formData, discord_url: e.target.value })}
                      className="retro-input bg-slate-900/50 border-slate-600 focus:border-gaming-primary h-9 sm:h-10 text-sm"
                      placeholder="https://discord.gg/monserveur"
                    />
                  </div>
                </div>

                <div className="mt-3 sm:mt-4">
                  <Label htmlFor="description" className="text-slate-300 font-mono text-xs sm:text-sm">
                    {t('editServer.description')}
                  </Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(content) => setFormData({ ...formData, description: content })}
                    placeholder={t('editServer.descriptionPlaceholder')}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button type="button" variant="outline" onClick={onClose} className="h-9 sm:h-10 text-sm order-2 sm:order-1">
                  {t('button.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="edit-server-form"
                  disabled={isEditingServer}
                  className="shadow-lg bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold h-9 sm:h-10 text-sm order-1 sm:order-2"
                >
                  {isEditingServer ? t('editServer.saving') : t('editServer.save')}
                </Button>
              </div>
            </TabsContent>

            {/* Onglet : Media / Tags / Events */}
            <TabsContent value="media" className="space-y-3 sm:space-y-4 md:space-y-6 mt-3 sm:mt-4 md:mt-6">
              {/* Images du serveur */}
              <div className="bg-slate-800/50 p-3 sm:p-4 md:p-6 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-gaming-primary" />
                  <h3 className="font-bold text-gaming-primary font-mono text-sm sm:text-base">{t('editServer.serverImages')}</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <Label className="text-slate-300 font-mono text-xs sm:text-sm">{t('editServer.serverLogo')}</Label>
                    <ImageUpload currentImageUrl={formData.logo_image_url} onImageUpload={handleLogoUpload} type="logo" />
                  </div>
                </div>

                {/* Bannière - Avec état verrouillé pour non-prestige */}
                <div
                  className={`p-3 sm:p-4 md:p-6 rounded-lg border mt-4 ${
                    isServerPrestige ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-800/30 border-slate-700/30 opacity-75'
                  }`}
                >
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4 flex-wrap">
                    {isServerPrestige ? (
                      <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                    ) : (
                      <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
                    )}
                    <h3 className={`font-bold font-mono text-sm sm:text-base ${isServerPrestige ? 'text-yellow-500' : 'text-slate-500'}`}>
                      {t('editServer.serverBanner')}
                    </h3>
                    <span
                      className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full ${
                        isServerPrestige ? 'bg-yellow-500/20 text-yellow-300' : 'bg-slate-600/20 text-slate-400'
                      }`}
                    >
                      {isServerPrestige ? 'PRESTIGE' : t('editServer.locked')}
                    </span>
                  </div>

                  {isServerPrestige ? (
                    <p className="text-xs sm:text-sm text-slate-400 mb-3 sm:mb-4">{t('editServer.bannerAvailableWithPrestige')}</p>
                  ) : (
                    <p className="text-xs sm:text-sm text-slate-500 mb-3 sm:mb-4">{t('editServer.upgradeForBanner')}</p>
                  )}

                  <div className={isServerPrestige ? '' : 'pointer-events-none opacity-50'}>
                    <ImageUpload currentImageUrl={formData.banner_image_url} onImageUpload={handleBannerUpload} type="banner" />
                  </div>
                </div>

                <div className="mt-4 sm:mt-6">
                  <Label className="text-slate-300 font-mono text-xs sm:text-sm">{t('editServer.descriptionImage')}</Label>
                  <ImageUpload
                    currentImageUrl={formData.description_image_url}
                    onImageUpload={handleDescriptionImageUpload}
                    type="description"
                  />
                </div>
              </div>

              {/* Tags */}
              <div className="bg-slate-800/50 p-3 sm:p-4 md:p-6 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-gaming-primary" />
                  <h3 className="font-bold text-gaming-primary font-mono text-sm sm:text-base">{t('editServer.tags')}</h3>
                </div>
                <TagSelector selectedTags={formData.tags} onTagsChange={(tags) => setFormData({ ...formData, tags })} />
              </div>

              {/* Événements */}
              <div className="bg-slate-800/50 p-3 sm:p-4 md:p-6 rounded-lg border border-slate-700/50">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                  <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 text-gaming-primary" />
                  <h3 className="font-bold text-gaming-primary font-mono text-sm sm:text-base">{t('editServer.events')}</h3>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <EventDisplay serverId={server?.id || ''} variant="detail" isServerPrestige={isServerPrestige} canDelete={true} />
                  <EventSelector
                    serverId={server?.id || ''}
                    onEventAdded={() => {}}
                    isServerPrestige={isServerPrestige}
                    isInEditMode={true}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button type="button" variant="outline" onClick={onClose} className="h-9 sm:h-10 text-sm order-2 sm:order-1">
                  {t('button.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="edit-server-form"
                  disabled={isEditingServer}
                  className="shadow-lg bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold h-9 sm:h-10 text-sm order-1 sm:order-2"
                >
                  {isEditingServer ? t('editServer.saving') : t('editServer.save')}
                </Button>
              </div>
            </TabsContent>

            {/* Onglet : Intégrations */}
            <TabsContent value="integrations" className="space-y-3 sm:space-y-4 md:space-y-6 mt-3 sm:mt-4 md:mt-6">
              {/* Webhooks */}
              <WebhookManager serverId={server?.id || ''} serverName={server?.name || ''} />

              {/* Votifier pour Minecraft uniquement */}
              <VotifierManager
                serverId={server?.id || ''}
                serverName={server?.name || ''}
                gameType={games?.find((g) => g.id === formData.game_id)?.name}
              />

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button type="button" variant="outline" onClick={onClose} className="h-9 sm:h-10 text-sm order-2 sm:order-1">
                  {t('button.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="edit-server-form"
                  disabled={isEditingServer}
                  className="shadow-lg bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold h-9 sm:h-10 text-sm order-1 sm:order-2"
                >
                  {isEditingServer ? t('editServer.saving') : t('editServer.save')}
                </Button>
              </div>
            </TabsContent>

            {/* Onglet : Prestige */}
            <TabsContent value="prestige" className="space-y-3 sm:space-y-4 md:space-y-6 mt-3 sm:mt-4 md:mt-6">
              {/* Couleur personnalisée - SEULEMENT POUR PRESTIGE */}
              <div
                className={`p-3 sm:p-4 md:p-6 rounded-lg border ${
                  isServerPrestige
                    ? 'bg-gradient-to-r from-yellow-500/10 to-yellow-600/10 border-yellow-500/30'
                    : 'bg-slate-800/30 border-slate-700/30'
                }`}
              >
                <div className="flex items-center justify-between mb-3 sm:mb-4 flex-wrap gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <Crown className={`w-4 h-4 sm:w-5 sm:h-5 ${isServerPrestige ? 'text-yellow-400' : 'text-slate-500'}`} />
                    <h3 className={`font-bold font-mono text-sm sm:text-base ${isServerPrestige ? 'text-yellow-400' : 'text-slate-500'}`}>
                      {t('editServer.prestigeCustomization')}
                    </h3>
                  </div>
                  {!isServerPrestige && (
                    <div className="flex items-center gap-1.5 sm:gap-2 text-slate-500">
                      <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="text-xs sm:text-sm">Prestige requis</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="custom_color"
                    className={`font-mono text-xs sm:text-sm ${isServerPrestige ? 'text-slate-300' : 'text-slate-500'}`}
                  >
                    {t('editServer.customColor')}
                  </Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.custom_color || '#F59E0B'}
                      onChange={(e) => setFormData({ ...formData, custom_color: e.target.value })}
                      disabled={!isServerPrestige}
                      className={`w-14 h-14 sm:w-16 sm:h-16 rounded-lg border-2 p-1 bg-transparent ${
                        isServerPrestige
                          ? 'border-yellow-500/50 hover:border-yellow-400 cursor-pointer'
                          : 'border-slate-700 cursor-not-allowed opacity-50'
                      }`}
                      style={{ colorScheme: 'dark' }}
                    />
                    <Input
                      type="text"
                      value={formData.custom_color}
                      onChange={(e) => setFormData({ ...formData, custom_color: e.target.value })}
                      className={`flex-1 border h-9 sm:h-10 text-sm ${
                        isServerPrestige
                          ? 'bg-slate-900/50 border-slate-600 focus:border-yellow-400 text-white'
                          : 'bg-slate-900/30 border-slate-700 text-slate-500 cursor-not-allowed'
                      }`}
                      placeholder="#F59E0B"
                      disabled={!isServerPrestige}
                    />
                  </div>
                  {!isServerPrestige && <p className="text-[10px] sm:text-xs text-slate-500 mt-2">{t('editServer.upgradeForColor')}</p>}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
                <Button type="button" variant="outline" onClick={onClose} className="h-9 sm:h-10 text-sm order-2 sm:order-1">
                  {t('button.cancel')}
                </Button>
                <Button
                  type="submit"
                  form="edit-server-form"
                  disabled={isEditingServer}
                  className="shadow-lg bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold h-9 sm:h-10 text-sm order-1 sm:order-2"
                >
                  {isEditingServer ? t('editServer.saving') : t('editServer.save')}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </form>
        {/* -------- FIN FORM GLOBAL -------- */}
      </DialogContent>
    </Dialog>
  );
};
